(function () {
  'use strict';

  if (window.__sfAgentSafeOffset) return;

  var DEFAULT_BOTTOM = 24;
  var COLLISION_GAP = 12;
  var originalStyles = new WeakMap();
  var ownStyleText = new WeakMap();
  var currentFloating = null;
  var currentCard = null;
  var currentHost = null;
  var observedCard = null;
  var observedFloating = null;
  var observedShadowRoot = null;
  var observedAncestors = [];
  var framePending = false;

  var resizeObserver = 'ResizeObserver' in window
    ? new ResizeObserver(scheduleUpdate)
    : null;
  function observedAttributesChanged(records) {
    if (records.some(function (record) {
      return !(record.attributeName === 'style' && ownStyleText.has(record.target)
        && record.target.style.cssText === ownStyleText.get(record.target));
    })) scheduleUpdate();
  }
  var shadowObserver = new MutationObserver(observedAttributesChanged);
  var ancestorObserver = new MutationObserver(observedAttributesChanged);

  function visibleRect(element) {
    if (!element) return null;

    var style = window.getComputedStyle(element);
    var rect = element.getBoundingClientRect();

    return style.display !== 'none'
      && style.visibility !== 'hidden'
      && parseFloat(style.opacity || '1') > 0.05
      && rect.width > 0
      && rect.height > 0
      && rect.bottom > 0
      && rect.top < window.innerHeight ? rect : null;
  }

  function findAgentFloatingElement(host) {
    if (!host || !host.shadowRoot) return null;

    var triggers = Array.prototype.slice.call(
      host.shadowRoot.querySelectorAll('[role="button"]')
    );
    var trigger = triggers.find(function (element) {
      return (element.getAttribute('aria-label') || '').indexOf('Einkaufshilfe') !== -1;
    });

    if (!trigger) return null;

    var element = trigger;
    while (element && element !== host.shadowRoot) {
      if (window.getComputedStyle(element).position === 'fixed') return element;
      element = element.parentElement;
    }

    return null;
  }

  function rememberStyles(element) {
    if (originalStyles.has(element)) return;

    originalStyles.set(element, {
      bottom: element.style.getPropertyValue('bottom'),
      bottomPriority: element.style.getPropertyPriority('bottom'),
      marginBottom: element.style.getPropertyValue('margin-bottom'),
      marginBottomPriority: element.style.getPropertyPriority('margin-bottom')
    });
  }

  function writeStyle(element, name, value, priority) {
    if (value) {
      if (element.style.getPropertyValue(name) !== value
        || element.style.getPropertyPriority(name) !== (priority || '')) {
        element.style.setProperty(name, value, priority);
        ownStyleText.set(element, element.style.cssText);
      }
    } else if (element.style.getPropertyValue(name)) {
      element.style.removeProperty(name);
      ownStyleText.set(element, element.style.cssText);
    }
  }

  function restoreStyles(element) {
    if (!element || !originalStyles.has(element)) return;

    var original = originalStyles.get(element);

    writeStyle(element, 'bottom', original.bottom, original.bottomPriority);
    writeStyle(element, 'margin-bottom', original.marginBottom, original.marginBottomPriority);
  }

  function setObservedElements(card, floating) {
    if (!resizeObserver) return;

    if (observedCard !== card) {
      if (observedCard) resizeObserver.unobserve(observedCard);
      observedCard = card;
      if (observedCard) resizeObserver.observe(observedCard);
    }

    if (observedFloating !== floating) {
      if (observedFloating) resizeObserver.unobserve(observedFloating);
      observedFloating = floating;
      if (observedFloating) resizeObserver.observe(observedFloating);
    }
  }

  function observeAgent(host) {
    var shadowRoot = host && host.shadowRoot;
    if (shadowRoot === observedShadowRoot) return;

    shadowObserver.disconnect();
    if (observedShadowRoot) {
      observedShadowRoot.removeEventListener('load', stylesheetSettled, true);
      observedShadowRoot.removeEventListener('error', stylesheetSettled, true);
    }
    observedShadowRoot = shadowRoot || null;
    if (!shadowRoot) return;
    shadowRoot.addEventListener('load', stylesheetSettled, true);
    shadowRoot.addEventListener('error', stylesheetSettled, true);
    shadowObserver.observe(shadowRoot, {
      childList: true,
      subtree: true,
      attributes: true
    });
  }

  function observeAncestors(card, host, floating) {
    var ancestors = [];
    [card, host, floating].forEach(function (element) {
      while (element) {
        if (ancestors.indexOf(element) === -1) ancestors.push(element);
        element = element.parentElement;
      }
    });
    if (ancestors.length === observedAncestors.length
      && ancestors.every(function (element, i) { return element === observedAncestors[i]; })) return;

    ancestorObserver.disconnect();
    observedAncestors = ancestors;
    ancestors.forEach(function (element) {
      ancestorObserver.observe(element, {
        attributes: true
      });
    });
  }

  function isStylesheet(element) {
    if (!element || element.nodeType !== 1) return false;
    var tag = element.tagName.toLowerCase();
    if (tag === 'style') return true; // Includes styles with asynchronous @import.
    if (tag !== 'link') return false;
    var rel = ' ' + (element.getAttribute('rel') || '').toLowerCase().replace(/\s+/g, ' ') + ' ';
    return rel.indexOf(' stylesheet ') !== -1
      || (rel.indexOf(' preload ') !== -1
        && (element.getAttribute('as') || '').toLowerCase() === 'style');
  }

  function stylesheetSettled(event) {
    if (currentHost && isStylesheet(event.target)) scheduleUpdate();
  }

  function isRelevantMount(node) {
    if (!node || node.nodeType !== 1) return false;
    if (node === currentCard || node === currentHost
      || (node.contains && ((currentCard && node.contains(currentCard))
        || (currentHost && node.contains(currentHost))))) return true;

    var selector = '#ads-agent-host, .product-sticky-form__card';
    if ((node.matches && node.matches(selector))
      || (node.querySelector && node.querySelector(selector))) return true;

    // Stylesheets can move a mounted agent without changing its size. Check
    // rel as a token list, including stylesheets mounted inside a wrapper.
    return !!currentHost && (isStylesheet(node)
      || (node.querySelectorAll && Array.prototype.some.call(
        node.querySelectorAll('style, link'), isStylesheet)));
  }

  function documentChanged(records) {
    // Checking the shadow-root identity does not read style or layout. A root
    // attached after the host is discovered at the next mutation, as before.
    // Inside that root, the scoped observer catches later trigger mounting.
    if (currentHost && currentHost.shadowRoot !== observedShadowRoot
      && (currentHost.shadowRoot || observedShadowRoot)) {
      scheduleUpdate();
      return;
    }
    // Preserve the generic geometry fallback without RO only while an agent
    // exists. When it is absent, unrelated SDK mutations must remain idle.
    if (!resizeObserver && currentHost) {
      scheduleUpdate();
      return;
    }
    if (records.some(function (record) {
      return (currentHost && record.target.matches && record.target.matches('style'))
        || (currentHost && currentCard && currentCard.contains(record.target))
        || (currentHost && currentHost.contains(record.target))
        || Array.prototype.some.call(record.addedNodes, isRelevantMount)
        || Array.prototype.some.call(record.removedNodes, isRelevantMount);
    })) scheduleUpdate();
  }

  function update() {
    framePending = false;

    var card = document.querySelector('.product-sticky-form__card');
    var host = document.getElementById('ads-agent-host');
    var floating = findAgentFloatingElement(host);

    currentCard = card;
    currentHost = host;
    observeAgent(host);
    setObservedElements(host ? card : null, floating);
    observeAncestors(host ? card : null, host, floating);

    if (currentFloating && currentFloating !== floating) {
      restoreStyles(currentFloating);
    }
    currentFloating = floating;

    if (!floating) return;
    rememberStyles(floating);

    var cardRect = visibleRect(card);
    if (!cardRect) {
      restoreStyles(floating);
      return;
    }

    var agentRect = floating.getBoundingClientRect();
    var horizontalOverlap = Math.min(cardRect.right, agentRect.right)
      - Math.max(cardRect.left, agentRect.left);

    if (horizontalOverlap <= 8) {
      restoreStyles(floating);
      return;
    }

    var requiredBottom = Math.ceil(window.innerHeight - cardRect.top + COLLISION_GAP);
    var maximumBottom = Math.max(
      DEFAULT_BOTTOM,
      Math.floor(window.innerHeight - agentRect.height - COLLISION_GAP)
    );
    var safeBottom = Math.max(
      DEFAULT_BOTTOM,
      Math.min(requiredBottom, maximumBottom)
    );

    writeStyle(floating, 'bottom', safeBottom + 'px', 'important');
    writeStyle(floating, 'margin-bottom', '0px', 'important');
  }

  function scheduleUpdate() {
    if (framePending) return;
    framePending = true;
    window.requestAnimationFrame(update);
  }

  var documentObserver = new MutationObserver(documentChanged);
  documentObserver.observe(document.documentElement, {
    childList: true,
    subtree: true
  });

  window.addEventListener('scroll', scheduleUpdate, { passive: true });
  window.addEventListener('resize', scheduleUpdate, { passive: true });
  document.addEventListener('shopify:section:load', scheduleUpdate);
  document.addEventListener('visibilitychange', scheduleUpdate);
  document.addEventListener('load', stylesheetSettled, true);
  document.addEventListener('error', stylesheetSettled, true);

  window.__sfAgentSafeOffset = {
    update: update,
    schedule: scheduleUpdate
  };

  scheduleUpdate();
})();
